import { maybe, Maybe } from '@passionware/monads';
import { CustomFieldType } from '__types__';
import {
  convertBooleanFilterToApi,
  convertEnumFilterToApi,
  convertEnumFilterToArrayApi,
  convertNumberFilterToApi,
  convertStringFilterToApi
} from 'v5/platform/filters/filterConverters';
import { customFilter } from '../standard-date-filter/custom-filter';
import {
  CustomFieldAnyFilter,
  CustomFieldBooleanFilter,
  CustomFieldCurrencyFilter,
  CustomFieldLongTextFilter,
  CustomFieldMeasurementFilter,
  CustomFieldMultiSelectFilter,
  CustomFieldNumberFilter,
  CustomFieldPercentageFilter,
  CustomFieldShortTextFilter,
  CustomFieldSingleSelectFilter,
  CustomFieldUrlFilter
} from './custom-fields-filters.types';

export const customFieldFiltersToHttp = {
  SHORT_TEXT: (filter: Maybe<CustomFieldShortTextFilter>) => {
    return filter ? convertStringFilterToApi(filter) : null;
  },
  LONG_TEXT: (filter: Maybe<CustomFieldLongTextFilter>) => {
    return filter ? convertStringFilterToApi(filter) : null;
  },
  URL: (filter: Maybe<CustomFieldUrlFilter>) => {
    return filter ? convertStringFilterToApi(filter) : null;
  },
  NUMBER: (filter: Maybe<CustomFieldNumberFilter>) => {
    return filter ? convertNumberFilterToApi(filter) : null;
  },
  PERCENTAGE: (filter: Maybe<CustomFieldPercentageFilter>) => {
    return filter ? convertNumberFilterToApi(filter) : null;
  },
  BOOLEAN: (filter: Maybe<CustomFieldBooleanFilter>) => {
    return filter ? convertBooleanFilterToApi(filter) : null;
  },
  DATE: customFilter.createHttpConverter(),
  DATE_RANGE: customFilter.createHttpConverter(),
  SINGLE_SELECT: (filter: Maybe<CustomFieldSingleSelectFilter>) => {
    return filter ? convertEnumFilterToApi(filter) : null;
  },
  MULTI_SELECT: (filter: Maybe<CustomFieldMultiSelectFilter>) => {
    return filter ? convertEnumFilterToArrayApi(filter) : null;
  },
  CURRENCY: (filter: Maybe<CustomFieldCurrencyFilter>) => {
    return filter ? convertNumberFilterToApi(filter) : null;
  },
  MEASUREMENT: (filter: Maybe<CustomFieldMeasurementFilter>) => {
    return filter ? convertNumberFilterToApi(filter) : null;
  },
  MEASUREMENT_NEW: (filter: Maybe<CustomFieldMeasurementFilter>) => {
    return filter ? convertNumberFilterToApi(filter) : null;
  }
};

/**
 * Mapping of field names to custom field types
 * e.g., { "custom_1": CustomFieldType.CURRENCY, "custom_2": CustomFieldType.SINGLE_SELECT }
 */
export type FieldNameTypeMapping = Record<string, CustomFieldType>;

/**
 * Embed the browsing-scope workspace lens on a converted custom-field HTTP filter.
 * The backend reads this from the filter object — not from a sibling entity
 * `enterprise_workspace_id` filter.
 */
export function withCustomFieldWorkspaceLens(
  converted: unknown,
  workspaceId: number | null
): Record<string, unknown> {
  if (Array.isArray(converted)) {
    return { $eq: converted, enterprise_workspace_id: workspaceId };
  }
  if (converted && typeof converted === 'object') {
    return {
      ...(converted as Record<string, unknown>),
      enterprise_workspace_id: workspaceId
    };
  }
  return { $eq: converted, enterprise_workspace_id: workspaceId };
}

/**
 * Convert custom field filters to HTTP format using field type mapping
 * @param fieldMapping - Mapping of field names to custom field types
 * @param filters - Object containing custom field filters (can be nullable)
 * @param workspaceId - browsing lens (`null` = global values only)
 */
export function convertCustomFieldFiltersToHttp(
  fieldMapping: FieldNameTypeMapping,
  filters: Record<string, Maybe<CustomFieldAnyFilter>>,
  workspaceId: number | null
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  Object.entries(filters).forEach(([fieldName, filterValue]) => {
    if (maybe.isAbsent(filterValue)) {
      return;
    }
    const fieldType = fieldMapping[fieldName];

    if (fieldType && fieldType in customFieldFiltersToHttp) {
      const converter = customFieldFiltersToHttp[fieldType];
      const converted = converter(filterValue);
      if (maybe.isAbsent(converted)) {
        return;
      }
      result[fieldName] = withCustomFieldWorkspaceLens(converted, workspaceId);
    }
  });

  return result;
}
